// La hoja que aparece antes de bajar — ADR-2026-09-14-configuracion-de-descarga.
//
// Es UNA sola hoja para las dos preguntas que puede haber (marca de agua y formato de
// salida), y no dos modales encadenadas: dos hojas seguidas para un click de descarga
// es de las peores cosas que se le pueden hacer a un flujo que hoy funciona.
//
// De ahí la única sutileza del archivo: cuando hay UNA sola pregunta, un tap elige y
// sigue —el comportamiento que ya tenía el selector de marca de agua, que no se toca—;
// cuando hay dos, las opciones se marcan y hace falta el botón de abajo. El usuario no
// tiene que enterarse de la regla: en el primer caso no ve ningún botón.

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Crop, Image as ImageIcon, Stamp } from 'lucide-react';
import { useIsDesktop } from '../../pages/edicionGrafica/lib/useIsDesktop';
import type { WatermarkChoice } from '../../utils/watermarkConfig';
import {
  ORIGINAL,
  type ConfigDeDescarga,
  type EleccionDeFormato,
  type PerfilDeDescarga,
} from '../../utils/configDeDescarga';

export interface OpcionesDeDescarga {
  marcaDeAgua: WatermarkChoice;
  formato: EleccionDeFormato;
}

export interface PropsDelChooser {
  open: boolean;
  /** Qué preguntar. Si las dos son `false` la hoja no se abre. */
  preguntarMarcaDeAgua: boolean;
  /** La config a ofrecer como alternativa al original; `null` = no se pregunta. */
  configDeFormato: ConfigDeDescarga | null;
  /** Elecciones con las que abre (las últimas que hizo en este dispositivo). */
  inicial: OpcionesDeDescarga;
  /** Cuántos archivos se van a bajar: cambia el título y nada más. */
  cantidad?: number;
  onConfirmar: (o: OpcionesDeDescarga) => void;
  onClose: () => void;
}

/** "1000 × 1500 · WebP · hasta 200 KB" */
export function describirFormato(p: PerfilDeDescarga): string {
  const partes = [`${p.ancho} × ${p.alto}`, p.formato.toUpperCase()];
  if (p.pesoMaxKB !== null) partes.push(`hasta ${p.pesoMaxKB} KB`);
  return partes.join(' · ');
}

/** Cómo se llama un perfil en la hoja: su nombre, o la medida si no le pusieron uno. */
export function nombreDelPerfil(p: PerfilDeDescarga): string {
  return p.nombre.trim() || describirFormato(p);
}

export function OpcionesDeDescargaChooser({
  open,
  preguntarMarcaDeAgua,
  configDeFormato,
  inicial,
  cantidad,
  onConfirmar,
  onClose,
}: PropsDelChooser) {
  const isDesktop = useIsDesktop();
  const [elegido, setElegido] = useState<OpcionesDeDescarga>(inicial);

  // Cada apertura arranca de la última elección guardada, no de la de la vez pasada
  // dentro de esta misma pantalla.
  useEffect(() => {
    if (open) setElegido(inicial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;
  const secciones = (preguntarMarcaDeAgua ? 1 : 0) + (configDeFormato ? 1 : 0);
  if (secciones === 0) return null;
  const unSoloTap = secciones === 1;

  const opcion = (
    seleccionada: boolean,
    Icon: typeof Stamp,
    label: string,
    detalle: string | null,
    alElegir: () => void,
    key: string = label,
  ) => (
    <button
      key={key}
      type="button"
      onClick={alElegir}
      className={`w-full flex items-center gap-3 py-2.5 px-3.5 rounded-md text-left transition ${
        seleccionada
          ? 'border-[1.5px] border-brand-primary bg-[#1644EB0A]'
          : 'border border-neutral-200 bg-white hover:bg-neutral-100'
      }`}
    >
      <Icon
        className={`w-[18px] h-[18px] shrink-0 ${seleccionada ? 'text-brand-primary' : 'text-neutral-600'}`}
      />
      <span className="min-w-0">
        <span className="block text-[14px] font-medium text-brand-black">{label}</span>
        {detalle && <span className="block text-[12px] text-neutral-500 truncate">{detalle}</span>}
      </span>
      {seleccionada && (
        <Check className="w-[18px] h-[18px] ml-auto shrink-0 text-brand-primary" strokeWidth={2.5} />
      )}
    </button>
  );

  /** Elegir en modo "un solo tap" cierra y descarga; si no, sólo marca. */
  const elegir = (parcial: Partial<OpcionesDeDescarga>) => {
    const siguiente = { ...elegido, ...parcial };
    setElegido(siguiente);
    if (unSoloTap) onConfirmar(siguiente);
  };

  const titulo =
    cantidad && cantidad > 1 ? `Descargar ${cantidad} imágenes` : 'Descargar imagen';

  const rotulo = (texto: string) =>
    secciones > 1 ? (
      <p className="text-[11.5px] uppercase tracking-wide font-medium text-neutral-500 mt-3 mb-1.5">
        {texto}
      </p>
    ) : null;

  const card = (
    <div
      className={
        isDesktop
          ? 'w-[340px] bg-white rounded-lg shadow-lg p-4'
          : 'w-full bg-white rounded-t-lg shadow-lg px-4 pt-2 pb-6'
      }
      onClick={(e) => e.stopPropagation()}
    >
      {!isDesktop && <div className="w-9 h-1 rounded-full bg-neutral-200 mx-auto mb-3.5" />}
      <p className="text-[15px] font-semibold text-brand-black">{titulo}</p>

      {preguntarMarcaDeAgua && (
        <>
          {rotulo('Marca de agua')}
          <div className="flex flex-col gap-2 mt-3 first:mt-0">
            {opcion(elegido.marcaDeAgua === 'con', Stamp, 'Con marca de agua', null, () =>
              elegir({ marcaDeAgua: 'con' }),
            )}
            {opcion(elegido.marcaDeAgua === 'sin', ImageIcon, 'Sin marca de agua', null, () =>
              elegir({ marcaDeAgua: 'sin' }),
            )}
          </div>
        </>
      )}

      {configDeFormato && (
        <>
          {rotulo('Formato')}
          <div className={`flex flex-col gap-2 ${secciones > 1 ? '' : 'mt-3'}`}>
            {opcion(
              elegido.formato === ORIGINAL,
              ImageIcon,
              'Original',
              'Como salió, sin recortar',
              () => elegir({ formato: ORIGINAL }),
            )}
            {/* Un botón por formato guardado. Con nombre se muestra el nombre y la
                medida abajo; sin nombre, la medida es el nombre. */}
            {configDeFormato.perfiles.map((p) =>
              opcion(
                elegido.formato === p.id,
                Crop,
                nombreDelPerfil(p),
                p.nombre.trim() ? describirFormato(p) : null,
                () => elegir({ formato: p.id }),
                p.id,
              ),
            )}
          </div>
        </>
      )}

      {unSoloTap ? (
        <p className="text-[12px] text-neutral-500 mt-2.5">
          Recordamos tu última elección para la próxima.
        </p>
      ) : (
        <button
          type="button"
          onClick={() => onConfirmar(elegido)}
          className="w-full mt-3.5 py-2.5 px-5 rounded-md bg-brand-primary text-white text-[14px] font-medium transition hover:opacity-90"
        >
          Descargar
        </button>
      )}
    </div>
  );

  return createPortal(
    <div
      className={`fixed inset-0 z-[220] bg-[#02062B80] backdrop-blur-sm flex ${
        isDesktop ? 'items-center justify-center p-6' : 'items-end'
      }`}
      onClick={onClose}
    >
      {card}
    </div>,
    document.body,
  );
}
